import React, { useState, useEffect } from 'react';
import { Shield, UserPlus, Trash2, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { authHelper } from '../utils/authHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminManagers() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/admins`, {
        headers: authHelper.getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setAdmins(data.admins || []);
      } else {
        setError('Error al cargar administradores');
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    
    if (!newEmail) {
      setError('Por favor ingresa un email');
      return;
    }

    setAdding(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/api/admin/admins`, {
        method: 'POST',
        headers: authHelper.getAuthHeaders(),
        body: JSON.stringify({ email: newEmail })
      });

      if (response.ok) {
        setSuccess(`Privilegios de admin otorgados a ${newEmail}`);
        setNewEmail('');
        fetchAdmins();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Error al agregar admin');
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveAdmin = async (email) => {
    if (!window.confirm(`¿Remover privilegios de admin de ${email}?`)) return;

    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/api/admin/admins/${email}`, {
        method: 'DELETE',
        headers: authHelper.getAuthHeaders()
      });

      if (response.ok) {
        setSuccess(`Privilegios removidos de ${email}`);
        fetchAdmins();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Error al remover admin');
      }
    } catch (err) {
      setError('Error de conexión');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#C5A14C]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Gestión de Administradores</h2>
        <p className="text-white/60">Otorga o revoca privilegios de administrador a usuarios</p>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-500" />
          <p className="text-emerald-400">{success}</p>
        </div>
      )}

      {/* Add Admin Form */}
      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus className="w-5 h-5 text-[#C5A14C]" />
          <h3 className="text-lg font-bold text-white">Agregar Administrador</h3>
        </div>

        <form onSubmit={handleAddAdmin} className="flex gap-3">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="email@ejemplo.com"
            className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#C5A14C]"
            disabled={adding}
          />
          <button
            type="submit"
            disabled={adding}
            className="bg-[#C5A14C] text-[#02040A] font-bold rounded-lg px-6 py-3 hover:bg-[#B08F3C] transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {adding ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Agregando...
              </>
            ) : (
              <>
                <UserPlus className="w-5 h-5" />
                Agregar
              </>
            )}
          </button>
        </form>

        <p className="text-xs text-white/40 mt-3">
          El usuario debe existir primero. Ingresa el email de un usuario registrado.
        </p>
      </div>

      {/* Admins List */}
      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-[#C5A14C]" />
          <h3 className="text-lg font-bold text-white">
            Administradores Actuales ({admins.length})
          </h3>
        </div>

        {admins.length === 0 ? (
          <p className="text-white/40 text-center py-8">No hay administradores</p>
        ) : (
          <div className="space-y-3">
            {admins.map((admin) => (
              <div
                key={admin.email || admin.user_id}
                className="bg-white/5 rounded-lg p-4 flex items-center justify-between border border-white/10"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#C5A14C]/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-[#C5A14C]" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{admin.username || admin.email}</p>
                    <p className="text-sm text-white/60">{admin.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {admin.is_primary ? (
                    <span className="text-xs bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full border border-purple-500/30">
                      🔒 Admin Principal
                    </span>
                  ) : (
                    <button
                      onClick={() => handleRemoveAdmin(admin.email)}
                      className="text-red-400 hover:text-red-300 p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                      title="Remover privilegios de admin"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
          <div className="space-y-2">
            <p className="text-blue-300 font-semibold">Información Importante</p>
            <ul className="text-sm text-blue-200/80 space-y-1">
              <li>• Los administradores tienen acceso completo al sistema</li>
              <li>• Pueden ver usuarios, suscripciones y configuración</li>
              <li>• El admin principal no puede ser removido</li>
              <li>• No puedes removerte a ti mismo</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
